/**
 * SitePublic 모델 - 서버와의 통신을 위한 공개 인터페이스
 */

/**
 * 위치 정보 인터페이스
 */
export interface ILocation {
  coordinates: [number, number]; // [경도, 위도]
  crs: {
    properties: { name: string };
    type: string;
  };
  type: string;
}

/**
 * 회사 정보 인터페이스
 */
export interface ICompanyPublic {
  seq: number | null;
  companyName: string;
}

/**
 * 프로젝트 정보 인터페이스
 */
export interface IProjectPublic {
  seq: number | null;
  name: string;
}

/**
 * 담당자 정보 인터페이스
 */
export interface ICompanyManagerPublic {
  seq: number | null;
  name: string;
  position: string;
  companySeq: number | null;
  companyName: string;
}

/**
 * 담당자-사이트 관계 정보 인터페이스
 */
export interface ICompanyManagerSiteRelationPublic {
  seq: number | null;
  managerSeq: number | null;
  siteSeq: number | null;
  manager: ICompanyManagerPublic | null;
}

/**
 * 사이트 공개 정보 인터페이스
 */
export interface ISitePublic {
  seq: number | null;
  name: string;
  address: string;
  establishedDate: string | null;
  pvModuleMaker: string;
  powerCapacity: number;
  pvModuleCount: number;
  pvModuleOutput: number;
  description: string;
  rotation: number | null;
  location: ILocation;
  ownerCompany: ICompanyPublic;
  operationCompany: ICompanyPublic;
  managers: ICompanyManagerPublic[];
  status: string;
  type: string;
  project: IProjectPublic;
  panelProductSeq: number | null;
}

/**
 * 기본값 객체들
 */
export const SitePublicDefaults = {
  location: {
    coordinates: [0, 0],
    crs: { properties: { name: 'EPSG:4326' }, type: 'name' },
    type: 'Point'
  } as ILocation,
  company: {
    seq: null,
    companyName: ''
  } as ICompanyPublic,
  project: {
    seq: null,
    name: ''
  } as IProjectPublic,
  manager: {
    seq: null,
    name: '',
    position: '',
    companySeq: null,
    companyName: ''
  } as ICompanyManagerPublic,
  site: {
    seq: null,
    name: '',
    address: '',
    establishedDate: null,
    pvModuleMaker: '',
    powerCapacity: 0,
    pvModuleCount: 0,
    pvModuleOutput: 0,
    description: '',
    rotation: null,
    location: { coordinates: [0, 0], crs: { properties: { name: 'EPSG:4326' }, type: 'name' }, type: 'Point' },
    ownerCompany: { seq: null, companyName: '' },
    operationCompany: { seq: null, companyName: '' },
    managers: [],
    status: 'active',
    type: 'solar',
    project: { seq: null, name: '' },
    panelProductSeq: null
  } as ISitePublic
};

/**
 * 팩토리 함수들
 */
export const SitePublicFactory = {
  /**
   * 빈 사이트 객체 생성
   */
  createEmpty(): ISitePublic {
    return { ...SitePublicDefaults.site };
  },
  
  /**
   * API 응답에서 사이트 객체 생성
   */
  fromApiResponse(apiResponse: any): ISitePublic {
    if (!apiResponse) return this.createEmpty();
    
    return {
      seq: apiResponse.seq || apiResponse.id || null,
      name: apiResponse.name || '',
      address: apiResponse.address || '',
      establishedDate: apiResponse.establishedDate || null,
      pvModuleMaker: apiResponse.pvModuleMaker || '',
      powerCapacity: apiResponse.powerCapacity || 0,
      pvModuleCount: apiResponse.pvModuleCount || 0,
      pvModuleOutput: apiResponse.pvModuleOutput || 0,
      description: apiResponse.description || '',
      rotation: apiResponse.rotation || null,
      location: apiResponse.location || SitePublicDefaults.location,
      ownerCompany: apiResponse.ownerCompany || SitePublicDefaults.company,
      operationCompany: apiResponse.operationCompany || SitePublicDefaults.company,
      managers: apiResponse.managers || [],
      status: apiResponse.status || 'active',
      type: apiResponse.type || 'solar',
      project: apiResponse.project || SitePublicDefaults.project,
      panelProductSeq: apiResponse.panelProductSeq || null
    };
  },
  
  /**
   * 배열 형태의 API 응답에서 사이트 객체 배열 생성
   */
  fromApiResponseArray(apiResponseArray: any[]): ISitePublic[] {
    if (!Array.isArray(apiResponseArray)) return [];
    return apiResponseArray.map(item => this.fromApiResponse(item));
  }
};

/**
 * 기본 export
 */
export default {
  SitePublicDefaults,
  SitePublicFactory
};
